/**
 * DAG Executor — dependency-aware task graph for agent orchestration.
 *
 * Runs independent nodes in parallel, waits on dependencies.
 * Supports retries, timeouts, and error propagation.
 */

import { withTimeout } from "./timeout";

/** A node in the execution DAG. */
export interface DAGNode {
  /** Unique identifier for this node. */
  id: string;
  /** IDs of nodes that must complete before this one starts. */
  dependsOn: string[];
  /** Execute the node's work. Receives outputs from upstream dependencies. */
  execute: (inputs: Record<string, string>) => Promise<string>;
  /** Number of retry attempts on failure (default 1 = no retry). */
  retries?: number;
  /** Timeout in milliseconds (default: no timeout). */
  timeout?: number;
}

/** Result of a DAG execution. */
export interface DAGResult {
  /** Outputs from each completed node. */
  outputs: Record<string, string>;
  /** Errors from failed nodes. */
  errors: Record<string, Error>;
}

export class DAGExecutor {
  /**
   * Execute a DAG of nodes.
   * Runs independent nodes in parallel, respects dependencies.
   * Retries failed nodes up to their retry count.
   */
  async run(nodes: DAGNode[]): Promise<DAGResult> {
    // Build lookup maps
    const nodeMap: Record<string, DAGNode> = {};
    const dependents: Record<string, string[]> = {};
    const inDegree: Record<string, number> = {};

    for (const node of nodes) {
      nodeMap[node.id] = node;
      inDegree[node.id] = node.dependsOn.length;
      for (const dep of node.dependsOn) {
        if (!dependents[dep]) dependents[dep] = [];
        dependents[dep].push(node.id);
      }
    }

    // Cycle detection via Kahn's algorithm (topological sort)
    const tempDegree = { ...inDegree };
    const queue = Object.keys(tempDegree).filter((id) => tempDegree[id] === 0);
    const sorted: string[] = [];

    while (queue.length > 0) {
      const id = queue.shift()!;
      sorted.push(id);
      for (const dep of dependents[id] || []) {
        tempDegree[dep]--;
        if (tempDegree[dep] === 0) queue.push(dep);
      }
    }

    if (sorted.length !== nodes.length) {
      throw new Error("DAG contains a cycle");
    }

    // Execute in waves
    const outputs: Record<string, string> = {};
    const errors: Record<string, Error> = {};
    const completed = new Set<string>();
    const remaining = new Set(nodes.map((n) => n.id));

    while (remaining.size > 0) {
      // Find nodes whose dependencies are all completed
      const ready: string[] = [];
      for (const id of remaining) {
        const node = nodeMap[id];
        const depsReady = node.dependsOn.every((d) => completed.has(d));
        if (depsReady) ready.push(id);
      }

      if (ready.length === 0) break; // Should not happen after cycle check

      // Run ready nodes in parallel
      await Promise.all(
        ready.map(async (id) => {
          const node = nodeMap[id];
          remaining.delete(id);

          // Check if any dependency failed
          const failedDep = node.dependsOn.find((d) => errors[d]);
          if (failedDep) {
            errors[id] = new Error(`Skipped: dependency "${failedDep}" failed`);
            completed.add(id);
            return;
          }

          // Build inputs from upstream outputs
          const inputs: Record<string, string> = {};
          for (const dep of node.dependsOn) {
            if (outputs[dep] !== undefined) inputs[dep] = outputs[dep];
          }

          // Execute with retries
          const maxAttempts = node.retries ?? 1;
          let lastError: Error | null = null;

          for (let attempt = 0; attempt < maxAttempts; attempt++) {
            try {
              let result: string;
              if (node.timeout) {
                result = await withTimeout(node.execute(inputs), node.timeout, `Node "${id}"`);
              } else {
                result = await node.execute(inputs);
              }
              outputs[id] = result;
              lastError = null;
              break;
            } catch (e) {
              lastError = e instanceof Error ? e : new Error(String(e));
            }
          }

          if (lastError) {
            errors[id] = lastError;
          }
          completed.add(id);
        }),
      );
    }

    return { outputs, errors };
  }
}
